"use client";

import { Button } from "./button";
import { ArrowRight } from "lucide-react";
import createGlobe from "cobe";
import { useCallback, useEffect, useRef, useState } from "react";
import { cn } from "../../lib/utils";

export default function Featured_05() {
  return (
    <section className="relative w-full mx-auto overflow-hidden rounded-3xl bg-muted border border-gray-200 dark:border-gray-800 shadow-md px-6 py-16 md:px-16 md:py-24 mt-48">
      <div className="flex flex-col-reverse items-center justify-between gap-10 md:flex-row">
        <div className="z-10 max-w-xl text-left">
          <h1 className="text-3xl font-normal text-gray-900 dark:text-white">
            Build with <span className="text-primary">Ruixen UI</span>{" "}
            <span className="text-gray-500 dark:text-gray-400">
              Empower your team with fast, elegant, and scalable UI components. Ruixen UI brings
              simplicity and performance to your modern apps.
            </span>
          </h1>
          <Button className="mt-6 inline-flex items-center gap-2 rounded-full bg-foreground px-5 py-2 text-sm font-semibold text-background transition hover:bg-black">
            Join Today <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
          <div className="relative h-[180px] w-full max-w-xl">
            <Globe className="absolute -bottom-20 -right-40 scale-150" />
        </div>
      </div>
    </section>
  );
}

const GLOBE_CONFIG = {
  width: 800,
  height: 800,
  onRender: () => {},
  devicePixelRatio: 2,
  phi: 0,
  theta: 0.3,
  dark: 0,
  diffuse: 0.4,
  mapSamples: 16000,
  mapBrightness: 1.2,
  baseColor: [1, 1, 1],
  markerColor: [251 / 255, 100 / 255, 21 / 255],
  glowColor: [1, 1, 1],
  markers: [
    { location: [14.5995, 120.9842], size: 0.03 },
    { location: [19.076, 72.8777], size: 0.1 },
    { location: [23.8103, 90.4125], size: 0.05 },
    { location: [30.0444, 31.2357], size: 0.07 },
    { location: [39.9042, 116.4074], size: 0.08 },
    { location: [-23.5505, -46.6333], size: 0.1 },
    { location: [19.4326, -99.1332], size: 0.1 },
    { location: [40.7128, -74.006], size: 0.1 },
    { location: [34.6937, 135.5022], size: 0.05 },
    { location: [41.0082, 28.9784], size: 0.06 },
  ],
};

export function Globe({ className, config = GLOBE_CONFIG }) {
  const phiRef = useRef(0);
  let width = 0;
  // Use a callback ref to avoid mutating a frozen ref object
  const canvasRef = useRef(null);
  const setCanvasRef = (node) => {
    canvasRef.current = node;
  };
  // Simplify pointer interaction without mutable refs that can cause issues
  const [isInteracting, setIsInteracting] = useState(false);
  const [pointerStartX, setPointerStartX] = useState(0);
  const [r, setR] = useState(0);

  const updatePointerInteraction = (value) => {
    if (value === null) {
      setIsInteracting(false);
      if (canvasRef.current) canvasRef.current.style.cursor = "grab";
    } else {
      setIsInteracting(true);
      setPointerStartX(value);
      if (canvasRef.current) canvasRef.current.style.cursor = "grabbing";
    }
  };

  const updateMovement = (clientX) => {
    if (isInteracting) {
      const delta = clientX - pointerStartX;
      setR(delta / 200);
    }
  };

  const onRender = useCallback(
    (state) => {
      if (!isInteracting) phiRef.current += 0.005;
      state.phi = phiRef.current + r;
      state.width = width * 2;
      state.height = width * 2;
    },
    [r, isInteracting]
  );

  const onResize = () => {
    if (canvasRef.current) {
      width = canvasRef.current.offsetWidth;
    }
  };

  useEffect(() => {
    window.addEventListener("resize", onResize);
    onResize();
    const globe = createGlobe(canvasRef.current, {
      ...config,
      width: width * 2,
      height: width * 2,
      onRender,
    });
    setTimeout(() => (canvasRef.current.style.opacity = "1"));
    return () => globe.destroy();
  }, []);

  return (
    <div className={cn("absolute inset-0 mx-auto aspect-[1/1] w-full max-w-[600px]", className)}>
      <canvas
        className={cn("size-full opacity-0 transition-opacity duration-500 [contain:layout_paint_size]")}
        ref={setCanvasRef}
        onPointerDown={(e) => updatePointerInteraction(e.clientX)}
        onPointerUp={() => updatePointerInteraction(null)}
        onPointerOut={() => updatePointerInteraction(null)}
        onMouseMove={(e) => updateMovement(e.clientX)}
        onTouchMove={(e) => e.touches[0] && updateMovement(e.touches[0].clientX)}
      />
    </div>
  );
}
